/* src/App.js */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
    // Import routing creation/provider components
    createBrowserRouter,
    RouterProvider,
    Navigate,
    useNavigate,
    useLocation, // *** Import useLocation ***
    Outlet
} from 'react-router-dom';
// *** ADD FontAwesome imports needed for Install Button ***
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';


// --- Context Providers ---
import { ProfileProvider, useProfile } from './contexts/ProfileContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { GameProvider, GameContext } from './contexts/GameContext';
// *** NEW: Import CustomGameProvider ***
import { CustomGameProvider } from './contexts/customGame/CustomGameContext'; // Path to your new context
import { LoadingProvider, useLoading as useLoadingContextHook } from './contexts/LoadingContext';
// *** Import the new PWA Install Context Provider and Hook ***
import { PWAInstallProvider, usePWAInstall } from './contexts/PWAInstallContext';
// *** Import the new PWA Update Context Provider and Hook ***
import { PWAUpdateProvider, usePWAUpdate } from './contexts/PWAUpdateContext';


// --- UI Components ---
import SidebarLayout from './components/ui/SidebarLayout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AddToHomeScreenModal from './components/ui/AddToHomeScreenModal'; // Keep iOS A2HS Modal import
// *** Import the new Android Modal ***
import AndroidA2HSModal from './components/ui/AndroidA2HSModal';
// *** Import the new Update Modal ***
import UpdateAvailableModal from './components/ui/UpdateAvailableModal';


// --- Screen/Page Components ---
import ProfileDashboard from './components/ProfileDashboard';
import GameScreen from './components/GameScreen';
// **** MODIFIED: Import new Experimental pages, remove old Playground import ****
import ExperimentalPage from './components/experimental/ExperimentalPage';
import AudioNBackGamePage from './components/experimental/AudioNBackGamePage';
import PokerCardNBackGamePage from './components/experimental/pokerCardNBack/PokerCardNBackGamePage'; // <<<--- ADDED POKER GAME IMPORT
import AnalyticsPage from './components/analytics/AnalyticsPage';
import TutorialScreen from './components/tutorial/TutorialScreen';
import JournalPage from './components/journal/JournalPage';
import LoginScreen from './components/auth/LoginScreen';
import SignupScreen from './components/auth/SignupScreen';
import PasswordReset from './components/auth/PasswordReset';
import PasswordActionHandler from './components/auth/PasswordActionHandler';
import ProfileSelectionScreen from './components/profile/ProfileSelectionScreen';
import FriendsPage from './components/friends/FriendsPage';
import AccountPage from './components/account/AccountPage';
import ChallengeExplanationScreen from './components/game/ChallengeExplanationScreen';
import SettingsPage from './components/settings/SettingsPage';
import WelcomeSetup from './components/auth/WelcomeSetup';
import UsernameOnboardingScreen from "./components/auth/UsernameOnboardingScreen";
import AllSessionsPage from './components/sessions/AllSessionsPage'; // <<<--- ADDED IMPORT
import PositionNBackExactGamePage from './components/experimental/positionNBackExact/PositionNBackExactGamePage';
// *** NEW: Import Custom Game Components ***
import CustomGameSetupPage from './components/customGame/setup/CustomGameSetupPage';
import CustomGamePlayerScreen from './components/customGame/player/CustomGamePlayerScreen';
// --- NEW: Import ResubscribeScreen ---
import ResubscribeScreen from './components/auth/ResubscribeScreen';


// --- Testing Components ---
import TestingPage from './components/testing/TestingPage';
import CountingSpanSummaryPage from './components/testing/countingSpan/CountingSpanSummaryPage';
import CountingSpanTask from './components/testing/countingSpan/CountingSpanTask';
import CountingSpanInstructionsWrapper from './components/testing/countingSpan/CountingSpanInstructionsWrapper';

// --- AuthGuard Component ---
import AuthGuard from './AuthGuard';
// *** Import ALL needed utils (using RENAMED visit count function) ***
import {
    shouldShowAddToHomeScreenPrompt,
    markAddToHomeScreenPromptSeen,
    isAndroid,
    getAppVisitCount, // *** Use renamed function ***
    hasDismissedAndroidA2HSModal,
    markAndroidA2HSModalDismissed
} from './utils/pwaUtils';


// --- Route Wrapper Components ---
import {
    ReplayViewerWrapper,
    PublicReplayViewerWrapper,
    JournalEntryViewerWrapper
} from './RouteWrappers';

// --- **** NEW: Import Admin Components **** ---
import AdminLogin from './components/admin/AdminLogin';
import AdminGuard from './components/admin/AdminGuard';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';


// --- Bridge Component (Connects useNavigate to GameContext) ---
// This component needs to be rendered INSIDE the RouterProvider context
// It allows GameContext actions to trigger navigation.
const NavigationProviderBridge = () => {
    const navigate = useNavigate(); // This hook requires Router context
    const gameContext = React.useContext(GameContext);
    const setNavigationHandler = gameContext?.setNavigationHandler;

    // Effect to pass the navigate function to the GameContext
    React.useEffect(() => {
        const capturedSetHandler = setNavigationHandler;
        // Ensure the handler function exists in the context
        if (capturedSetHandler && typeof capturedSetHandler === 'function') {
            capturedSetHandler(navigate); // Pass the navigate function
            // Cleanup function: Remove the handler when the component unmounts
            return () => {
                if (capturedSetHandler && typeof capturedSetHandler === 'function') {
                    capturedSetHandler(null); // Clear the handler reference
                }
            };
        }
    }, [setNavigationHandler, navigate]); // Re-run if handler or navigate changes

    return null; // This bridge component does not render any UI itself
};

// =====================================================
// === Helper Components for Routing Logic           ===
// =====================================================

// AuthWrapper: Protects public auth routes (e.g., /login) from already logged-in users.
const AuthWrapper = ({ children }) => {
    const { authUser, isLoading } = useProfile();
    const location = useLocation();
    
    // Show a spinner if the context is still resolving the initial user state.
    if (isLoading) {
        return <LoadingSpinner message="Verifying session..." />;
    }

    // After loading, if a user is found, redirect them to the central loading/redirect page.
    if (authUser) {
        console.log(`[AuthWrapper] User authenticated. Redirecting from ${location.pathname} to /loading...`);
        return <Navigate to="/loading" replace />;
    }

    // If not loading and no user, render the login/signup page.
    return children;
};

// *** NEW: Central loading and redirection component ***
const LoadingAndRedirect = () => {
    const { isLoading, activeProfileId } = useProfile();
    const navigate = useNavigate();

    useEffect(() => {
        // This effect will run whenever isLoading or activeProfileId changes.
        // We only want to act when isLoading becomes false.
        if (!isLoading) {
            const redirectTo = activeProfileId ? "/dashboard" : "/profile-selection";
            console.log(`[LoadingAndRedirect] Loading complete. Redirecting to ${redirectTo}`);
            navigate(redirectTo, { replace: true });
        }
    }, [isLoading, activeProfileId, navigate]);

    // While loading, display a full-screen spinner. This component persists until the redirect happens.
    return <LoadingSpinner message="Loading your profile..." />;
};


// LoggedInGuard: Protects routes requiring login but not necessarily a subscription or active profile.
// Used for onboarding flows, profile selection, and now resubscription.
const LoggedInGuard = ({ children }) => {
    const { authUser, isAuthLoading } = useProfile();
    const location = useLocation();

    // Display loading spinner while checking auth state
    if (isAuthLoading) {
        console.log(`[LoggedInGuard] Waiting for auth check at ${location.pathname}`);
        return <LoadingSpinner message="Verifying session..." />;
    }

    // If auth check complete and no user, redirect to login
    if (!authUser) {
        console.log(`[LoggedInGuard] User not logged in. Redirecting to login from ${location.pathname}`);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // If user is logged in, render the protected route (children)
    console.log(`[LoggedInGuard] User logged in. Accessing protected route: ${location.pathname}`);
    return children;
};

// RootRedirect: Handles the root ("/") path, redirecting based on auth status.
const RootRedirect = () => {
    const { authUser, activeProfileId, isLoading } = useProfile();

    if (isLoading) {
        console.log("[RootRedirect] Waiting for auth check...");
        return <LoadingSpinner message="Loading application..." />;
    }

    let targetPath = "/login";
    if (authUser) {
        targetPath = activeProfileId ? "/dashboard" : "/profile-selection";
    }
    console.log(`[RootRedirect] Auth check complete. Redirecting from '/' to ${targetPath}`);
    return <Navigate to={targetPath} replace />;
};

// =====================================================
// === Auth Switching Components                     ===
// =====================================================
// These components allow using useNavigate within the route definitions
// to pass navigation functions down to the screen components.

const LoginSwitcher = () => {
    const navigate = useNavigate();
    const onSwitchToReset = useCallback(() => navigate('/reset-password', { replace: true }), [navigate]);
    return <LoginScreen onSwitchToReset={onSwitchToReset} />;
};

const SignupSwitcher = () => {
    const navigate = useNavigate();
    const onSwitchToLogin = useCallback(() => navigate('/login', { replace: true }), [navigate]);
    return <SignupScreen onSwitchToLogin={onSwitchToLogin} />;
};

const PasswordResetSwitcher = () => {
    const navigate = useNavigate();
    const onSwitchToLogin = useCallback(() => navigate('/login', { replace: true }), [navigate]);
    return <PasswordReset onSwitchToLogin={onSwitchToLogin} />;
};

// =====================================================
// === Main Application Layout Component             ===
// =====================================================
// This layout component wraps the main application routes.
// It ensures the NavigationProviderBridge is rendered within the router's context,
// allowing GameContext to use navigation.
// ** Includes iOS A2HS Modal logic (NOW CHECKS VISIT COUNT) **
const MainAppLayout = () => {
    const [showA2HSModal, setShowA2HSModal] = useState(false);

    useEffect(() => {
        // *** Check visit count here ***
        const currentVisitCount = getAppVisitCount();
        console.log(`[MainAppLayout iOS Check] Current visits: ${currentVisitCount}`);

        // Check if the iOS prompt should be shown
        if (shouldShowAddToHomeScreenPrompt() && currentVisitCount >= 3) {
             console.log('[MainAppLayout iOS Check] Conditions met (iOS, visits>=3, not seen). Showing modal soon.');
            // Optional: Add a delay so it doesn't appear immediately
            const timer = setTimeout(() => {
               setShowA2HSModal(true);
            }, 3000); // Show after 3 seconds

            return () => clearTimeout(timer); // Cleanup timer on unmount
        } else {
             console.log(`[MainAppLayout iOS Check] Conditions not met. iOS Prompt Check: ${shouldShowAddToHomeScreenPrompt()}, Visits: ${currentVisitCount}`);
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    const handleCloseA2HSModal = () => {
      setShowA2HSModal(false);
      markAddToHomeScreenPromptSeen(); // Mark as seen in localStorage
    };

    return (
        <>
            {/* Renders the bridge to connect GameContext to navigation */}
            <NavigationProviderBridge />
            {/* Renders the matched child route based on the current URL */}
            <Outlet />
            {/* Render the Add to Home Screen Modal */}
            <AddToHomeScreenModal isOpen={showA2HSModal} onClose={handleCloseA2HSModal} />
        </>
    );
};


// =====================================================
// === Router Definition using createBrowserRouter   ===
// =====================================================

// Define the application's routes using the object-based configuration
// required by createBrowserRouter.
const router = createBrowserRouter([
    // The top-level route uses MainAppLayout. All subsequent routes
    // will be rendered inside its <Outlet /> and have access to the bridge.
    {
        element: <MainAppLayout />,
        children: [
            // --- Publicly Accessible Routes ---
            // No authentication or guards needed for these.
            { path: "/share/replay/:sessionId", element: <PublicReplayViewerWrapper /> },
            { path: "/action", element: <PasswordActionHandler /> }, // Handles email verification, password reset links

            // --- Authentication Routes ---
            // Wrapped by AuthWrapper to handle redirection if user is already logged in.
            { path: "/login", element: <AuthWrapper><LoginSwitcher /></AuthWrapper> },
            { path: "/signup", element: <AuthWrapper><SignupSwitcher /></AuthWrapper> },
            { path: "/reset-password", element: <AuthWrapper><PasswordResetSwitcher /></AuthWrapper> },

            // --- **** NEW: Post-Login Loading Route **** ---
            // This route is protected by LoggedInGuard to ensure a user exists,
            // and it handles the final redirect after all data is loaded.
            { path: "/loading", element: <LoggedInGuard><LoadingAndRedirect /></LoggedInGuard> },

            // --- **** NEW: Admin Login Route **** ---
            // This route is specifically for admin login.
            { path: "/admin/login", element: <AdminLogin /> },

            // --- Post-Signup / Onboarding / Resubscribe Routes ---
            // Require user to be logged in (checked by LoggedInGuard).
            // These are typically full-screen flows before full app access or when forced to resubscribe.
            { path: "/welcome-setup", element: <LoggedInGuard><WelcomeSetup /></LoggedInGuard> },
            { path: "/onboarding/username", element: <LoggedInGuard><UsernameOnboardingScreen /></LoggedInGuard> },
            // --- NEW: Resubscribe route, protected by LoggedInGuard ---
            { path: "/resubscribe", element: <LoggedInGuard><ResubscribeScreen /></LoggedInGuard> },


            // --- **** NEW: Protected Admin Routes **** ---
            {
                // Use AdminGuard to protect this section
                element: <AdminGuard />,
                children: [
                    {
                        // Use AdminLayout for consistent admin UI
                        element: <AdminLayout />,
                        children: [
                            // Define routes within the admin panel
                            { path: "/admin/dashboard", element: <AdminDashboard /> },
                            // Default route within /admin redirects to dashboard
                            { path: "/admin", element: <Navigate to="/admin/dashboard" replace /> },
                        ]
                    }
                ]
            }, // --- End Protected Admin Routes ---


            // --- Protected Routes (Main Application) ---
            // These routes are first checked by AuthGuard (login & subscription & active profile).
            {
                element: <AuthGuard />, // Acts as a layout, checks permissions
                // Children are rendered within AuthGuard's Outlet if checks pass
                children: [
                    // --- Routes WITH Sidebar ---
                    // These routes also use the SidebarLayout component.
                    {
                        element: <SidebarLayout />, // Provides the sidebar UI
                        // Children are rendered within SidebarLayout's Outlet
                        children: [
                            // Profile Selection: Needs login, bypasses sub check (in AuthGuard)
                            { path: "/profile-selection", element: <ProfileSelectionScreen /> },
                            // Account & Settings: Need login, bypass sub check (in AuthGuard)
                            { path: "/account", element: <AccountPage /> },
                            { path: "/settings", element: <SettingsPage /> },
                            // Core App Features: Need login & active sub (checked by AuthGuard)
                            { path: "/dashboard", element: <ProfileDashboard /> },
                            { path: "/analytics", element: <AnalyticsPage /> },
                            { path: "/friends", element: <FriendsPage /> },
                            { path: "/journal", element: <JournalPage /> },
                            { path: "/journal/entry/:entryId", element: <JournalEntryViewerWrapper /> },
                            { path: "/testing", element: <TestingPage /> },
                            { path: "/testing/counting-span", element: <CountingSpanSummaryPage /> },
                            { path: "/testing/counting-span/tutorial", element: <CountingSpanInstructionsWrapper /> },
                            { path: "/sessions", element: <AllSessionsPage /> },
                            { path: "/experimental", element: <ExperimentalPage /> },
                            { path: "/custom-game/setup", element: <CustomGameSetupPage /> },
                            // Existing /choose-plan route used by SignupScreen for logged-in users
                            // (e.g., navigating from /account to change/start plan with an active profile)
                            // This route is inside AuthGuard and SidebarLayout.
                            { path: "/choose-plan", element: <SignupScreen /> },
                            // Index Route: Default route within the sidebar layout (redirects to dashboard)
                            { index: true, element: <Navigate to="/dashboard" replace /> },
                        ]
                    }, // End SidebarLayout Group

                    // --- Routes WITHOUT Sidebar (Still protected by AuthGuard) ---
                    // These routes are protected by AuthGuard but don't have the sidebar UI.
                    { path: "/game", element: <GameScreen /> },
                    { path: "/tutorial", element: <TutorialScreen /> },
                    { path: "/experimental/audio-n-back", element: <AudioNBackGamePage /> },
                    { path: "/experimental/poker-card-n-back", element: <PokerCardNBackGamePage /> },
                    { path: "/experimental/position-n-back-exact", element: <PositionNBackExactGamePage /> },
                    { path: "/challenge-start", element: <ChallengeExplanationScreen /> },
                    { path: "/replay/:sessionId", element: <ReplayViewerWrapper /> }, // Private replays
                    { path: "/testing/counting-span/run", element: <CountingSpanTask /> },
                    { path: "/custom-game/play", element: <CustomGamePlayerScreen /> },

                ] // End AuthGuard Children
            }, // End AuthGuard Route

            // --- Root Fallback Redirect ---
            // Catches any path not matched above within the MainAppLayout.
            // Redirects based on authentication status.
            { path: "/", element: <RootRedirect /> }, // Explicitly handle root
            { path: "*", element: <RootRedirect /> }, // Catch-all redirects

        ] // End MainAppLayout Children
    } // End MainAppLayout Route Configuration
]);


// =====================================================
// === AppWrapper and LocationReporter Structure     ===
// =====================================================

// *** New component to report location changes up ***
const LocationReporter = ({ onLocationChange }) => {
    const location = useLocation();

    useEffect(() => {
        onLocationChange(location.pathname);
    }, [location.pathname, onLocationChange]); // Trigger when pathname changes

    return null; // This component does not render anything
};

// AppWrapper contains PWA logic (Install Modals, Update Modals, Periodic Update Check) and renders the RouterProvider
// ** NEW: Added periodic SW update check **
const AppWrapper = () => {
    // --- State for Modals & Device/Visit Info ---
    const [showAndroidA2HSModal, setShowAndroidA2HSModal] = useState(false);
    const [isAndroidDevice, setIsAndroidDevice] = useState(false);
    const [visitCount, setVisitCount] = useState(0);
    const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

    // --- PWA Context Hooks ---
    const { deferredInstallPrompt, handleInstallClick: contextHandleInstallClick } = usePWAInstall();
    const { isUpdateAvailable, activateUpdate } = usePWAUpdate();

    // --- Ref for SW Update Check Throttling ---
    const lastUpdateCheckTimestamp = useRef(0); // Stores timestamp of the last check initiation

    // --- Constants for SW Update Check ---
    const CHECK_INTERVAL = 60 * 60 * 1000; // Check every hour (in milliseconds)
    const MIN_CHECK_INTERVAL = 5 * 60 * 1000; // Don't check more often than every 5 mins (in milliseconds)

    // --- Callback to update pathname state ---
    const handleLocationChange = useCallback((path) => {
        setCurrentPathname(path);
    }, []);

    // --- Effect to check device type and visit count on mount ---
    useEffect(() => {
        setIsAndroidDevice(isAndroid());
        setVisitCount(getAppVisitCount());
    }, []);

    // --- Effect for Periodic SW Update Check ---
    useEffect(() => {
        const performUpdateCheck = () => {
            const now = Date.now();
            // Throttle checks: Only proceed if MIN_CHECK_INTERVAL has passed since the last check started
            if (now - lastUpdateCheckTimestamp.current < MIN_CHECK_INTERVAL) {
                console.log('[PWA Update Check] Throttled, too soon since last check.');
                return;
            }

            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistration().then(reg => {
                    if (reg) {
                        // Optional: Check if an update check is already installing. Might prevent redundant checks
                        // if the interval/visibility triggers fire close together after an update was found.
                        if (reg.installing) {
                             console.log('[PWA Update Check] Skipping check: Update already installing.');
                             return;
                        }

                        console.log('[PWA Update Check] Performing periodic update check...');
                        lastUpdateCheckTimestamp.current = now; // Record timestamp when check *starts*
                        reg.update() // Attempt to update the service worker
                           .then(updatedReg => {
                               // reg.update() returns a promise resolving to the registration
                               // This log helps confirm the check completed via this trigger.
                               // The actual detection logic is handled by PWAUpdateContext's listeners.
                               if (updatedReg && updatedReg.installing) {
                                    console.log('[PWA Update Check] reg.update() found an installing worker.');
                               } else if (reg.waiting) {
                                    // This case means an update was already found and waiting, possibly by PWAUpdateContext's initial load check.
                                    console.log('[PWA Update Check] reg.update() completed, waiting worker was already present.');
                               } else {
                                    console.log('[PWA Update Check] reg.update() completed, no update found this time.');
                               }
                           })
                           .catch(err => {
                                console.error('[PWA Update Check] reg.update() failed:', err);
                                // Consider if you want to reset the timestamp on failure to allow retrying sooner.
                           });
                    } else {
                        console.log('[PWA Update Check] No SW registration found for periodic check.');
                    }
                }).catch(error => {
                     console.error('[PWA Update Check] Error getting registration:', error);
                });
            } else {
                console.log('[PWA Update Check] Service workers not supported, skipping periodic check.');
            }
        };

        // Set up the interval timer to call performUpdateCheck periodically
        console.log(`[PWA Update Check] Setting up periodic check interval: ${CHECK_INTERVAL / (60 * 1000)} minutes.`);
        const intervalId = setInterval(performUpdateCheck, CHECK_INTERVAL);

        // Add listener for visibility change to trigger check when tab becomes visible
        const handleVisibilityChange = () => {
            // Only check when the tab becomes visible, not when it's hidden
            if (document.visibilityState === 'visible') {
                console.log('[PWA Update Check] Tab became visible, triggering check (if not throttled).');
                performUpdateCheck(); // Reuse the throttled check function
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Perform an initial check shortly after the component mounts and stabilizes
        const initialCheckTimeout = setTimeout(() => {
             console.log('[PWA Update Check] Performing initial check after load...');
             performUpdateCheck();
        }, 15 * 1000); // Delay the initial check slightly (e.g., 15 seconds)


        // Cleanup function: This runs when the AppWrapper component unmounts
        return () => {
            console.log('[PWA Update Check] Cleaning up periodic check interval and visibility listener.');
            clearInterval(intervalId); // Stop the interval timer
            clearTimeout(initialCheckTimeout); // Clear the initial check timeout if it hasn't fired
            document.removeEventListener('visibilitychange', handleVisibilityChange); // Remove the event listener
        };
    }, []); // Empty dependency array ensures this effect runs only once on mount

    // --- Effect to check conditions and show ANDROID INSTALL modal ---
    useEffect(() => {
        // Don't check if the prompt isn't even available yet
        if (!deferredInstallPrompt) {
            if (showAndroidA2HSModal) setShowAndroidA2HSModal(false);
            return;
        }

        const promptAvailable = true;
        const dismissed = hasDismissedAndroidA2HSModal();
        // Log individual states BEFORE the check
        console.log(`[PWA Install Trigger - Values] Prompt: ${promptAvailable}, Android: ${isAndroidDevice}, Path: ${currentPathname}, Visits: ${visitCount}, Dismissed: ${dismissed}`);

        const canShowPrompt =
            isAndroidDevice &&
            currentPathname === '/dashboard' && // Check against state variable
            visitCount >= 3 &&
            !dismissed;

        // Log the final check result
        console.log(`[PWA Install Trigger Check Result] Can Show Modal: ${canShowPrompt}`);
        setShowAndroidA2HSModal(canShowPrompt);

    // Dependencies now include the local pathname state and the prompt from context
    }, [deferredInstallPrompt, isAndroidDevice, visitCount, currentPathname]); // Simplified dependencies


    // --- Callbacks for Modal Buttons ---
    const handleModalInstallClick = useCallback(() => {
        contextHandleInstallClick((outcome) => {
            console.log(`[AppWrapper] Install outcome: ${outcome}`);
        });
        setShowAndroidA2HSModal(false);
    }, [contextHandleInstallClick]);

    const handleDismissAndroidModal = useCallback(() => {
        console.log('[AppWrapper] User dismissed the Android A2HS modal.');
        markAndroidA2HSModalDismissed();
        setShowAndroidA2HSModal(false);
    }, []);

    const handleModalUpdateClick = useCallback(() => {
        console.log('[AppWrapper] User clicked update.');
        activateUpdate();
    }, [activateUpdate]);


    // --- Render Logic ---
    // Determine if the UpdateAvailableModal should be open
    const showUpdateModal = isUpdateAvailable && currentPathname === '/dashboard';

    return (
        <>
            {/* RouterProvider wraps the LocationReporter and the actual app routes */}
            <RouterProvider router={router}>
                 {/* Render LocationReporter inside RouterProvider to get location context */}
                 <LocationReporter onLocationChange={handleLocationChange} />
                 {/* Note: RouterProvider itself will render the matched route component (e.g., MainAppLayout) */}
            </RouterProvider>
            {/* Render the ANDROID INSTALL modal based on state managed here */}
             <AndroidA2HSModal
                isOpen={showAndroidA2HSModal}
                onClose={handleDismissAndroidModal}
                onInstall={handleModalInstallClick}
            />
            {/* Render the UPDATE modal based on state from context AND current pathname */}
            <UpdateAvailableModal
                isOpen={showUpdateModal}
                onUpdate={handleModalUpdateClick}
            />
        </>
    );

};


// =====================================================
// === FINAL Root Component (Entry Point)            ===
// =====================================================
// This now just sets up the context providers (including PWA Providers) and renders AppWrapper
const Root = () => {
    return (
        <LoadingProvider>
            <ProfileProvider>
                <SettingsProvider>
                    <NotificationProvider>
                        <GameProvider>
                            {/* Wrap CustomGameProvider AROUND standard GameProvider if CustomGame needs its own global context */}
                            {/* OR, CustomGamePlayerScreen can wrap its children if context is scoped only to custom game play */}
                            <CustomGameProvider> {/* NEW: CustomGameProvider Added Here */}
                                {/* Wrap AppWrapper with PWA Context Providers */}
                                <PWAInstallProvider>
                                      <PWAUpdateProvider>
                                          <AppWrapper />
                                      </PWAUpdateProvider>
                                </PWAInstallProvider>
                            </CustomGameProvider>
                        </GameProvider>
                    </NotificationProvider>
                </SettingsProvider>
            </ProfileProvider>
        </LoadingProvider>
    );
};


// Export the Root component as the default export
export default Root;